import {
  ChevronRight,
  CircleDollarSign,
  Clock,
  EllipsisVertical,
  Package,
  Trash2,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { OrderWithStock } from '@snootsvet/shared'
import {
  formatClinicPrice,
  formatName,
  matchesPredicate,
  ORDER_PREDICATES,
} from '@snootsvet/shared'

import { Alert, AlertDescription, AlertTitle } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/frontend/components/ui/table'
import {
  AppointmentGroupCell,
  computeAppointmentRowSpans,
} from '@/frontend/features/orders/appointment-group'
import {
  NotesDialog,
  TransferDialog,
} from '@/frontend/features/orders/checkout-screen/checkout-dialogs'
import { OrderAllocationForm } from '@/frontend/features/orders/checkout-screen/order-allocation.form'
import { usePrepareOrders } from '@/frontend/features/orders/hooks/use-prepare-orders'
import { useUpdateOrder } from '@/frontend/features/orders/hooks/use-update-order'
import { OrderStatusBadge, PaymentBadge } from '@/frontend/features/orders/order-badges'
import { OrderItemCell } from '@/frontend/features/orders/order-item-cell'
import { isOrderFeeUnzeroable, isOrderFeeZeroable } from '@/frontend/features/orders/orders.utils'
import {
  getCurrentAllocationStatus,
  StockAllocationBadge,
  stockWarningMessage,
} from '@/frontend/features/orders/stock-allocation.utils'
import { computeStockAllocation } from '@/frontend/lib/stock'

export interface CheckoutPendingSectionProps {
  patientId: string
  orders: OrderWithStock[]
  /** Whether a mutation is in progress elsewhere - disables the Prepare All button. */
  mutating: boolean
  onMutatingChange: (mutating: boolean) => void
}

export function CheckoutPendingSection({
  patientId,
  orders,
  mutating,
  onMutatingChange,
}: CheckoutPendingSectionProps) {
  const { mutate: updateOrder } = useUpdateOrder()
  const { mutateAsync: prepareOrders } = usePrepareOrders()

  const rowSpans = useMemo(() => computeAppointmentRowSpans(orders), [orders])

  // Compute FEFO allocation suggestions synchronously from the stock rows on each order.
  const stockByOrderId = useMemo(() => {
    const map = new Map<string, ReturnType<typeof computeStockAllocation>>()
    for (const order of orders) {
      const rows = order.product.stock.filter((s) => s.clinicId === order.clinicId)
      map.set(order.id, computeStockAllocation(rows, order.quantity))
    }
    return map
  }, [orders])

  const [preparingOrderIds, setPreparingOrderIds] = useState<string[] | null>(null)

  // ---- Dialog state ----

  const [notesTarget, setNotesTarget] = useState<{
    type: 'out_of_stock' | 'cancel'
    order: OrderWithStock
  } | null>(null)

  const [transferTarget, setTransferTarget] = useState<OrderWithStock | null>(null)

  const ordersWithIssues = orders.flatMap((order) => {
    const data = stockByOrderId.get(order.id)
    if (!data || data.stockStatus === 'ok') return []
    const reason = stockWarningMessage(data.stockStatus)
    if (!reason) return []
    return [{ order, reason }]
  })

  if (orders.length === 0) return null

  return (
    <section className="rounded-lg border border-border p-3 shadow-xs">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-headline-sm text-muted-foreground">
          <Package className="h-4 w-4" />
          Pending
        </h3>
        <Button
          disabled={mutating}
          onClick={() => setPreparingOrderIds(orders.map((order) => order.id))}
        >
          <Package className="h-4 w-4" />
          Prepare All
        </Button>
      </div>
      <Table size="sm" variant="borderless" className="table-fixed">
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead className="w-6" />
            <TableHead className="w-40">Item</TableHead>
            <TableHead className="w-14 text-center">Qty</TableHead>
            <TableHead className="w-28">Prescriber</TableHead>
            <TableHead>Instructions</TableHead>
            <TableHead className="w-36 text-center">Status</TableHead>
            <TableHead className="w-28 text-center">Payment</TableHead>
            <TableHead className="w-32 text-center">Batch</TableHead>
            <TableHead className="w-16 text-right">Discount</TableHead>
            <TableHead className="w-24 text-right">Price</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((order, idx) => {
            const data = stockByOrderId.get(order.id)
            const allocations = data?.allocations ?? []
            const stockStatus = data?.stockStatus ?? null
            const allocationStatus = getCurrentAllocationStatus(allocations, order.quantity)

            return (
              <TableRow key={order.id}>
                <TableCell />
                {rowSpans[idx] > 0 && (
                  <AppointmentGroupCell order={order} rowSpan={rowSpans[idx]} />
                )}
                <OrderItemCell order={order} />
                <TableCell className="text-center">{order.quantity}</TableCell>
                <TableCell
                  className="truncate text-muted-foreground"
                  title={order.treatment?.staff ? formatName(order.treatment.staff) : undefined}
                >
                  {order.treatment?.staff ? formatName(order.treatment.staff) : '—'}
                </TableCell>
                <TableCell
                  className="max-w-0 truncate text-muted-foreground"
                  title={order.treatment?.instructions ?? undefined}
                >
                  {order.treatment?.instructions ?? '—'}
                </TableCell>
                <TableCell className="text-center">
                  <OrderStatusBadge status={order.status} />
                </TableCell>
                <TableCell className="text-center">
                  <PaymentBadge order={order} />
                </TableCell>
                <TableCell className="text-center">
                  <StockAllocationBadge
                    allocations={allocations}
                    ready
                    allocationStatus={allocationStatus}
                    stockStatus={stockStatus}
                  />
                </TableCell>
                <TableCell className="text-right">
                  {order.discountBps > 0 ? `${order.discountBps / 100}%` : '-'}
                </TableCell>
                <TableCell className="text-right">
                  {formatClinicPrice(order.totalPrice, order.clinicId)}
                </TableCell>
                <TableCell className="text-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={<Button size="icon" variant="ghost" aria-label="Row actions" />}
                    >
                      <EllipsisVertical className="h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" size="sm">
                      <DropdownMenuItem
                        onClick={() => setPreparingOrderIds([order.id])}
                        disabled={mutating}
                      >
                        <Package className="h-4 w-4" />
                        Prepare
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setTransferTarget(order)}>
                        <ChevronRight className="h-4 w-4" />
                        Transfer Clinic
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setNotesTarget({ type: 'out_of_stock', order })}
                      >
                        <Clock className="h-4 w-4" />
                        Out of Stock
                      </DropdownMenuItem>
                      {isOrderFeeZeroable(order) && (
                        <DropdownMenuItem
                          onClick={() => updateOrder({ id: order.id, action: 'zero_fee' })}
                          disabled={mutating}
                        >
                          <CircleDollarSign className="h-4 w-4" />
                          Zero Fee
                        </DropdownMenuItem>
                      )}
                      {isOrderFeeUnzeroable(order) && (
                        <DropdownMenuItem
                          onClick={() => updateOrder({ id: order.id, action: 'unzero_fee' })}
                          disabled={mutating}
                        >
                          <CircleDollarSign className="h-4 w-4" />
                          Restore Fee
                        </DropdownMenuItem>
                      )}
                      {matchesPredicate(order, ORDER_PREDICATES.cancellable) && (
                        <DropdownMenuItem
                          variant="destructive"
                          onClick={() => setNotesTarget({ type: 'cancel', order })}
                        >
                          <Trash2 className="h-4 w-4" />
                          Cancel
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      {ordersWithIssues.length > 0 && (
        <Alert variant="warning" className="mt-3">
          <AlertTitle>Some orders need manual preparation</AlertTitle>
          <AlertDescription>
            <ul className="mt-1 flex flex-col gap-0.5">
              {ordersWithIssues.map(({ order, reason }) => (
                <li key={order.id}>
                  {order.product.name} × {order.quantity} — {reason}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <NotesDialog
        open={notesTarget !== null}
        type={notesTarget?.type ?? 'cancel'}
        productName={notesTarget?.order.product.name ?? ''}
        defaultValue={notesTarget?.order.notes ?? ''}
        onConfirm={(notes) => {
          if (!notesTarget) return
          updateOrder({
            id: notesTarget.order.id,
            action: notesTarget.type,
            notes: notes || undefined,
          })
          setNotesTarget(null)
        }}
        onCancel={() => setNotesTarget(null)}
      />

      <TransferDialog
        open={transferTarget !== null}
        productName={transferTarget?.product.name ?? ''}
        onConfirm={(clinicId) => {
          if (!transferTarget) return
          updateOrder({ id: transferTarget.id, action: 'transfer', clinicId })
          setTransferTarget(null)
        }}
        onCancel={() => setTransferTarget(null)}
      />

      {preparingOrderIds && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open && !mutating) setPreparingOrderIds(null)
          }}
        >
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" nested>
            <DialogHeader>
              <DialogTitle>
                {preparingOrderIds.length > 1 ? 'Prepare Orders' : 'Prepare Order'}
              </DialogTitle>
              <DialogDescription>
                Only confirm once preparation is complete and the batches below match the stock
                used.
                <span className="mt-2 block">
                  For remote dispenses, we’ll automatically email the member 2 hours after their
                  items are prepared.
                </span>
              </DialogDescription>
            </DialogHeader>
            <OrderAllocationForm
              patientId={patientId}
              orderIds={preparingOrderIds}
              onSubmit={async (body) => {
                onMutatingChange(true)
                try {
                  await prepareOrders(body)
                  toast.success('Items prepared')
                  setPreparingOrderIds(null)
                } finally {
                  onMutatingChange(false)
                }
              }}
              onCancel={() => setPreparingOrderIds(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </section>
  )
}
